/**
 * A design-system release: its manifest names the uploaded bundle and
 * stylesheet by hash. The manifest is content-addressed too, and the site's
 * active design system (owner-authorized, on the server) is its hash — so a
 * release is immutable, and changing the design system is switching a pointer.
 */
import { CONTRACT_VERSION } from "@website-builder/ds-contract";

export interface DsManifest {
  readonly contractVersion: typeof CONTRACT_VERSION;
  readonly name: string;
  readonly bundleSha: string;
  readonly cssSha: string;
  readonly uploadedAt: string;
}

const SHA = /^[0-9a-f]{64}$/;

/** Canonical bytes (fixed key order), so equal manifests hash equal. */
export function encodeManifest(m: DsManifest): Uint8Array {
  const canonical = {
    contractVersion: m.contractVersion,
    name: m.name,
    bundleSha: m.bundleSha,
    cssSha: m.cssSha,
    uploadedAt: m.uploadedAt,
  };
  return new TextEncoder().encode(JSON.stringify(canonical));
}

export function parseManifest(bytes: Uint8Array): DsManifest | null {
  try {
    const m = JSON.parse(new TextDecoder().decode(bytes)) as Record<
      string,
      unknown
    >;
    if (
      m["contractVersion"] !== CONTRACT_VERSION ||
      typeof m["name"] !== "string" ||
      typeof m["bundleSha"] !== "string" ||
      !SHA.test(m["bundleSha"]) ||
      typeof m["cssSha"] !== "string" ||
      !SHA.test(m["cssSha"]) ||
      typeof m["uploadedAt"] !== "string"
    )
      return null;
    return m as unknown as DsManifest;
  } catch {
    return null;
  }
}
